import { useState } from 'react';
import moment from 'moment';
import { useFormik } from 'formik';
import { useMutation, useQueryClient } from 'react-query';
import * as yup from 'yup';

import {
  EVENT_START_TIME_TYPES,
  EVENT_END_TIME_TYPES,
  EVENT_TIME_TYPE__START,
  EVENT_TIME_TYPE__MIDDAY,
  EVENT_TIME_TYPE__END,
} from '../../constants';
import timeaway from '../../network/timeaway';
import { useLeaveTypes } from '../../hooks/useLeaveTypes';
import { useToast } from '../../utils/useToast';
import { Dialog } from '../../ui/Dialog';
import { FormGroup } from '../../ui/form/FormGroup';
import { Select } from '../../ui/form/Select';
import { Input } from '../../ui/form/Input';
import { Textarea } from '../../ui/form/Textarea';

type Props = {
  isOpen: boolean;
  onClose(): void;
};


// The API's time-of-day values for the labels the dialog shows.
const TIME_OF_DAY: Record<string, string> = {
  [EVENT_TIME_TYPE__START]: 'start_of_day',
  [EVENT_TIME_TYPE__MIDDAY]: 'midday',
  [EVENT_TIME_TYPE__END]: 'end_of_day',
};

const ERROR_MESSAGES: Record<string, string> = {
  OVERLAPPING_REQUESTS: 'This time is already booked.',
  INSUFFICIENT_BALANCE:
    'You do not have enough remaining balance for this leave type.',
  PAST_DATES_NOT_ALLOWED: 'You cannot request time off in the past.',
  FUTURE_DATES_NOT_ALLOWED:
    'You cannot request time off more than a year in advance.',
  INVALID_DURATION: 'This request has no working days in it.',
  NOTE_REQUIRED: 'This leave type needs a description.',
  PART_DAYS_NOT_ALLOWED: 'This leave type must be booked in whole days.',
  INVALID_LEAVE_TYPE: 'This leave type cannot be booked.',
};

/**
 * Requests time away from the calendar. It used an RTK Query API that was
 * never registered in the store and had no base URL, so nothing was sent.
 */
export function EventDialog({ isOpen, onClose }: Props) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [submitError, setSubmitError] = useState<string | null>(null);
  // The company's bookable leave types (public holidays are set by the
  // company, not requested).
  const { leaveTypes } = useLeaveTypes();
  const requestableTypes = leaveTypes.filter((type) => type.active);

  const createEvent = useMutation(
    (values: {
      type: string;
      start: string;
      startTimeOfDay: string;
      end: string;
      endTimeOfDay: string;
      description?: string;
    }) =>
      timeaway.bookTime(
        values.type,
        {
          date: moment(values.start).format('YYYY-MM-DD'),
          time: TIME_OF_DAY[values.startTimeOfDay],
        },
        {
          date: moment(values.end).format('YYYY-MM-DD'),
          time: TIME_OF_DAY[values.endTimeOfDay],
        },
        values.description?.trim() || undefined,
      ),
    {
      onSuccess: () => {
        toast({ variant: 'success', title: 'Time away requested.' });
        queryClient.invalidateQueries('home-calendar');
        closeDialog();
      },
      onError: (error: any) => {
        const status = error?.response?.data?.status;
        setSubmitError(
          ERROR_MESSAGES[status] ||
            error?.response?.data?.message ||
            'Unable to submit the request. Please try again.',
        );
      },
    },
  );

  const formik = useFormik({
    initialValues: {
      type: 'holiday_paid',
      start: '',
      startTimeOfDay: EVENT_START_TIME_TYPES[0],
      end: '',
      endTimeOfDay: EVENT_END_TIME_TYPES[0],
      description: '',
    },
    validationSchema: yup.object({
      type: yup.string().required(),
      start: yup.date().required(),
      startTimeOfDay: yup.string().required(),
      end: yup
        .date()
        .required()
        .min(yup.ref('start'), "End date can't be before start date"),
      endTimeOfDay: yup.string().required(),
      description: yup.string(),
    }),
    onSubmit: (values) => {
      setSubmitError(null);
      createEvent.mutate(values);
    },
  });

  function closeDialog() {
    formik.resetForm();
    setSubmitError(null);
    onClose();
  }

  function findError(fieldName: keyof typeof formik.initialValues) {
    return formik.touched[fieldName] && formik.errors[fieldName]
      ? formik.errors[fieldName]
      : undefined;
  }

  return (
    <Dialog
      isOpen={isOpen}
      onClose={closeDialog}
      onApply={formik.handleSubmit}
      title="Add event"
    >
      <form className="mb-4" onSubmit={formik.handleSubmit}>
        <div className="space-y-6 sm:space-y-5">
          <FormGroup label="Type" htmlFor="event-type">
            <Select
              id="event-type"
              name="type"
              value={formik.values.type}
              onChange={formik.handleChange}
            >
              {requestableTypes.map((type) => (
                <option key={type.key} value={type.key}>
                  {type.name}
                </option>
              ))}
            </Select>
          </FormGroup>
          <FormGroup label="Start date" htmlFor="event-start-date">
            <Input
              id="event-start-date"
              type="date"
              name="start"
              error={findError('start')}
              value={formik.values.start}
              onChange={formik.handleChange}
            />
          </FormGroup>
          <FormGroup label="Start time" htmlFor="event-start-time">
            <Select
              id="event-start-time"
              name="startTimeOfDay"
              value={formik.values.startTimeOfDay}
              onChange={formik.handleChange}
            >
              {EVENT_START_TIME_TYPES.map((type, index) => (
                <option key={index} value={type}>
                  {type}
                </option>
              ))}
            </Select>
          </FormGroup>
          <FormGroup label="End date" htmlFor="event-end-date">
            <Input
              id="event-end-date"
              type="date"
              name="end"
              error={findError('end')}
              value={formik.values.end}
              onChange={formik.handleChange}
            />
          </FormGroup>
          <FormGroup label="End time" htmlFor="event-end-time">
            <Select
              id="event-end-time"
              name="endTimeOfDay"
              value={formik.values.endTimeOfDay}
              onChange={formik.handleChange}
            >
              {EVENT_END_TIME_TYPES.map((type, index) => (
                <option key={index} value={type}>
                  {type}
                </option>
              ))}
            </Select>
          </FormGroup>
          {submitError && (
            <p role="alert" className="text-sm text-red-600">
              {submitError}
            </p>
          )}
          <FormGroup label="Description" htmlFor="event-description">
            <Textarea
              id="event-description"
              name="description"
              error={findError('description')}
              value={formik.values.description}
              onChange={formik.handleChange}
            />
          </FormGroup>
        </div>
      </form>
    </Dialog>
  );
}
